// Utilities to work with retail pricing rules of Ferretería El Bodegón (iter26).
//
// Regla de negocio:
//   - Al ingresar un producto, se sugiere un precio de venta tal que
//     * `precio_final = round(price, 2) + round(price * itbis, 2)` termine
//        en 0 o 5 y sin decimales,
//     * `price >= cost * 1.40`.
//   - El sistema guarda SIEMPRE `price` sin ITBIS. El POS lo recalcula.
//   - Si el usuario ingresa un precio con ITBIS, hay que revertirlo a
//     `price = round(final / (1 + itbis), 2)`.

export const ITBIS_RATE = 0.18;
export const MARGIN_STANDARD = 0.30;   // 30% recomendado
export const MARGIN_MIN = 0.05;        // 5% mínimo absoluto (menor requiere admin y es rechazado)
// Retro-compat (algunos componentes aún importan MIN_MARGIN)
export const MIN_MARGIN = MARGIN_STANDARD;

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Devuelve el precio final (con ITBIS) correspondiente a un precio sin ITBIS,
 * aplicando el mismo redondeo que el POS (round(subtotal,2) + round(itbis,2)).
 */
export function finalWithItbis(price, itbisApplies = true, rate = ITBIS_RATE) {
  const p = Number(price) || 0;
  if (!itbisApplies) return round2(p);
  return round2(round2(p) + round2(p * rate));
}

/**
 * Sugiere un precio de venta sin ITBIS tal que:
 *  - price >= cost * (1 + minMargin)
 *  - finalWithItbis(price) es entero y múltiplo de 5 (termina en 0 o 5)
 *
 * Itera desde el mínimo válido en pasos de 0.01 hasta encontrar el primer precio
 * que cumpla ambas condiciones. Este método garantiza cero drift porque valida
 * el resultado con la misma función de redondeo que usa el POS.
 */
export function suggestPriceFromCost(cost, itbisApplies = true, {
  itbisRate = ITBIS_RATE,
  minMargin = MARGIN_STANDARD,
  maxIterations = 800,
} = {}) {
  const c = Number(cost) || 0;
  if (c <= 0) return { price: 0, priceFinal: 0 };

  const minPriceNet = c * (1 + minMargin);
  // Empezar en el ceil de dos decimales para asegurar >= minPriceNet
  let p = Math.ceil(minPriceNet * 100) / 100;

  if (!itbisApplies) {
    // Sin ITBIS: redondear al siguiente múltiplo de 5
    const target = Math.max(5, Math.ceil(p / 5) * 5);
    return { price: target, priceFinal: target };
  }

  for (let i = 0; i < maxIterations; i++) {
    const f = finalWithItbis(p, true, itbisRate);
    if (Number.isInteger(f) && f % 5 === 0) {
      return { price: p, priceFinal: f };
    }
    p = round2(p + 0.01);
  }
  // Fallback improbable — devolver el último candidato
  return { price: p, priceFinal: finalWithItbis(p, true, itbisRate) };
}

/**
 * Convierte un precio con ITBIS a precio sin ITBIS (para guardar en la BD).
 * price_net = round(final / (1 + itbis), 2).
 */
export function priceFromFinal(finalPrice, itbisApplies = true, rate = ITBIS_RATE) {
  const f = Number(finalPrice) || 0;
  if (!itbisApplies) return round2(f);
  return round2(f / (1 + rate));
}

/**
 * Margen en % sobre costo, dado un precio de venta sin ITBIS.
 */
export function marginPct(price, cost) {
  const p = Number(price) || 0;
  const c = Number(cost) || 0;
  if (c <= 0) return null;
  return round2(((p - c) / c) * 100);
}

/**
 * Clasifica el margen sobre costo:
 *   - "ok":       markup >= 30% (estándar)
 *   - "warning":  5% <= markup < 30% (requiere confirmación admin)
 *   - "blocked":  markup < 5% (bloqueado)
 *   - "unknown":  costo o precio 0 → no evaluable
 */
export function classifyMargin(price, cost) {
  const p = Number(price) || 0;
  const c = Number(cost) || 0;
  if (c <= 0 || p <= 0) return { status: "unknown", markup: 0 };
  const markup = (p - c) / c;
  if (markup < MARGIN_MIN) return { status: "blocked", markup };
  if (markup < MARGIN_STANDARD) return { status: "warning", markup };
  return { status: "ok", markup };
}
